import { ReactElement } from 'react';
import { Control, FieldValues } from 'react-hook-form';
import { Box } from '@mui/material';
import BigNumber from 'bignumber.js';

import { LIQUID_STAKING_TOKEN_ADDRESS } from 'modules/api/const';
import { useApprove } from 'modules/api/hooks/useApprove';
import { GuardButton } from 'modules/auth/components/GuardButton';
import { Info } from 'modules/common/components/Info/Info';
import { NumberTextField } from 'modules/common/components/NumberTextField';
import { Summary } from 'modules/common/components/Summary';
import { SummaryItem } from 'modules/common/components/SummaryItem';
import {
  BUY_STAKE_MORE_LINK,
  DECIMAL_PLACES_SHORT,
  DEFAULT_DECIMAL_PLACES,
  ONE,
} from 'modules/common/const';
import stakeTokenIcon from 'modules/common/icons/stake-token-icon.svg';
import { useGlobalTranslation } from 'modules/i18n';
import { useLiquidStakingPool } from 'modules/liquidStaking/hooks/useLiquidStakingPool';
import { useStakeToken } from 'modules/liquidStaking/hooks/useStakeToken';

import { useStakeForm } from '../../hooks/useStakeForm';
import { translation } from './translation';
import { useStyles } from './useStyles';

export interface IStakeFormProps {
  onSubmit: (params: { amount: BigNumber; receiveAmount: BigNumber }) => void;
  isSubmitLoading: boolean;
}

export function StakeForm({
  onSubmit,
  isSubmitLoading,
}: IStakeFormProps): ReactElement {
  const { t, keys } = useGlobalTranslation(translation);
  const { classes } = useStyles();

  const {
    amount,
    balance,
    receiveAmount,
    incomeAmount,
    incomeAmountUSD,
    control,
    errors,
    onMaxClick,
    onSubmit: handleSubmit,
    isSubmitDisabled,
    apy,
  } = useStakeForm();

  const {
    isApproved,
    isDisabled: isApproveDisabled,
    isLoading: isApproveLoading,
    onApprove,
  } = useApprove({
    amount,
    poolAddress: LIQUID_STAKING_TOKEN_ADDRESS,
  });

  const { poolAddress } = useLiquidStakingPool();
  const { name: stakeTokenName, ratio, fee } = useStakeToken(poolAddress);

  return (
    <form
      className={classes.root}
      onSubmit={handleSubmit(amount =>
        onSubmit({ amount: new BigNumber(amount), receiveAmount }),
      )}
    >
      <NumberTextField
        balance={balance}
        control={control as unknown as Control<FieldValues>}
        error={!!errors.amount?.message}
        getMoreLink={BUY_STAKE_MORE_LINK}
        helperText={errors.amount?.message}
        label={t(keys.inputTokenLabel, { token: t(keys.tokens.main) })}
        name="amount"
        placeholder="0"
        unit={t(keys.tokens.main)}
        variant="filled"
        onMaxClick={onMaxClick}
      />

      <div className={classes.infos}>
        {!incomeAmount.isZero() && fee && (
          <Info>
            {t(
              keys.income,
              {
                value: incomeAmount.decimalPlaces(DEFAULT_DECIMAL_PLACES),
                usdValue:
                  incomeAmountUSD && !incomeAmountUSD.isZero()
                    ? t(keys.unit.$, {
                        value: incomeAmountUSD.decimalPlaces(0).toFormat(),
                      })
                    : '',
                apy: apy.decimalPlaces(2),
                fee,
                token: t(keys.tokens.main),
              },
              true,
            )}
          </Info>
        )}
      </div>

      <Summary className={classes.summary}>
        <SummaryItem
          label={t(keys.receiveLabel)}
          value={
            <>
              <Box alt="" component="img" minWidth={16} src={stakeTokenIcon} />

              <div className={classes.receiveValue}>
                {t(keys.unit.tokenValue, {
                  value: receiveAmount.isZero()
                    ? '0.00'
                    : receiveAmount.decimalPlaces(DECIMAL_PLACES_SHORT),
                  token: stakeTokenName,
                })}
              </div>
            </>
          }
        />

        <SummaryItem
          label={t(keys.exchangeRateLabel)}
          value={t(keys.rateValues, {
            input: t(keys.unit.tokenValue, {
              value: ONE.toFormat(),
              token: t(keys.tokens.main),
            }),
            output: t(keys.unit.tokenValue, {
              value: ratio.decimalPlaces(3).toFormat(),
              token: stakeTokenName,
            }),
          })}
        />
      </Summary>

      {isApproved ? (
        <GuardButton
          disabled={isSubmitDisabled}
          loading={isSubmitLoading}
          size="large"
          sx={theme => ({ marginTop: theme.spacing(6) })}
          type="submit"
          variant="outlined"
        >
          {t(keys.stake)}
        </GuardButton>
      ) : (
        <GuardButton
          disabled={isApproveDisabled || isSubmitDisabled}
          loading={isApproveLoading}
          size="large"
          sx={theme => ({ marginTop: theme.spacing(6) })}
          variant="outlined"
          onClick={() => onApprove()}
        >
          {t(keys.approve)}
        </GuardButton>
      )}
    </form>
  );
}
